import React from 'react';
import { Button } from '../components/ui/button';
import { Check, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import AnimatedSection from '../components/AnimatedSection';
import { pageTranslations, useLanguage } from '../i18n/pageTranslations';

const LiftingFacial = () => {
  const lang = useLanguage();
  const t = pageTranslations.liftingFacial[lang] || pageTranslations.liftingFacial.es;

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-[#8b7355] to-[#6d5a45]"></div>
        
        <div className="container mx-auto max-w-5xl">
          <AnimatedSection animation="fadeUp" className="text-center mb-8">
            <a href="tel:952764547" className="text-[#8b7355] font-semibold hover:underline">
              952 76 45 47
            </a>
          </AnimatedSection>
          
          <AnimatedSection animation="fadeUp" delay={0.1}>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-light text-[#1C1C1A] text-center mb-6 leading-tight">
              {t.title}
            </h1>
          </AnimatedSection>
          
          <AnimatedSection animation="fadeUp" delay={0.2}>
            <p className="text-lg md:text-xl text-text-secondary text-center mb-4 max-w-3xl mx-auto">
              {t.heroSubtitle}
            </p>
          </AnimatedSection>
          
          <AnimatedSection animation="fadeUp" delay={0.3}>
            <p className="text-lg text-text-secondary text-center mb-8">
              {t.bookConsult}
            </p>
          </AnimatedSection>
          
          <AnimatedSection animation="fadeUp" delay={0.4} className="text-center">
            <Button 
              onClick={() => scrollToSection('contacto')}
              className="bg-[#8b7355] hover:bg-[#6d5a45] text-white px-8 py-6 text-lg rounded transition-all duration-300"
            >
              {t.requestConsult}
            </Button>
          </AnimatedSection>
        </div>
      </section>

      {/* Before/After Section */}
      <section className="py-12 bg-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <AnimatedSection animation="fadeUp">
              <h2 className="text-2xl md:text-3xl font-light text-[#1C1C1A] text-center mb-8">
                {t.realResults}
              </h2>
            </AnimatedSection>
            
            {/* Caso 1 */}
            <AnimatedSection animation="fadeUp" delay={0.1}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="text-center">
                  <img 
                    src="/images/lifting-antes.jpg" 
                    alt={t.before}
                    className="w-full h-auto rounded-none shadow-lg"
                  />
                  <p className="mt-4 text-text-secondary font-medium text-lg">{t.before}</p>
                  <p className="text-text-muted text-sm">{t.preoperative}</p>
                </div>
                <div className="text-center">
                  <img 
                    src="/images/lifting-despues.jpg" 
                    alt={t.after}
                    className="w-full h-auto rounded-none shadow-lg"
                  />
                  <p className="mt-4 text-text-secondary font-medium text-lg">{t.after}</p>
                  <p className="text-text-muted text-sm">{t.twoWeeks}</p>
                </div>
              </div>
            </AnimatedSection>
            
            {/* Caso 2 */}
            <AnimatedSection animation="fadeUp" delay={0.2}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="text-center">
                  <img 
                    src="/images/lifting2-antes.jpg" 
                    alt={t.before}
                    className="w-full h-auto rounded-none shadow-lg"
                  />
                  <p className="mt-4 text-text-secondary font-medium text-lg">{t.before}</p>
                  <p className="text-text-muted text-sm">{t.preoperative}</p>
                </div>
                <div className="text-center">
                  <img 
                    src="/images/lifting2-despues.jpg" 
                    alt={t.after}
                    className="w-full h-auto rounded-none shadow-lg"
                  />
                  <p className="mt-4 text-text-secondary font-medium text-lg">{t.after}</p>
                  <p className="text-text-muted text-sm">{t.postoperative}</p>
                </div>
              </div>
            </AnimatedSection>
            
            {/* Caso 3 - Lifting de Cuello */}
            <AnimatedSection animation="fadeUp" delay={0.3}>
              <div className="mt-16 pt-12 border-t border-gray-200">
                <h3 className="text-xl md:text-2xl font-light text-[#1C1C1A] text-center mb-6">
                  {t.neckLift}
                </h3>
                <p className="text-center text-text-secondary mb-8">
                  {t.nextDayResult}
                </p>
                
                <div className="grid grid-cols-2 gap-4 mb-4 max-w-2xl mx-auto">
                  <div className="aspect-square overflow-hidden rounded-none shadow-lg">
                    <img 
                      src="/images/lifting-cuello-pre-lateral1.jpg" 
                      alt={t.before}
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                  <div className="aspect-square overflow-hidden rounded-none shadow-lg">
                    <img 
                      src="/images/lifting-cuello-pre-lateral2.jpg" 
                      alt={t.before}
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </div>
                <p className="text-center text-text-secondary font-medium mb-8">{t.before}</p>
                
                <div className="grid grid-cols-2 gap-4 mb-4 max-w-2xl mx-auto">
                  <div className="aspect-square overflow-hidden rounded-none shadow-lg">
                    <img 
                      src="/images/lifting-cuello-post-frontal.jpg" 
                      alt={t.after}
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                  <div className="aspect-square overflow-hidden rounded-none shadow-lg">
                    <img 
                      src="/images/lifting-cuello-post-lateral.jpg" 
                      alt={t.after}
                      className="w-full h-full object-cover object-center"
                    />
                  </div>
                </div>
                <p className="text-center text-text-secondary font-medium">{t.nextDay}</p>
              </div>
            </AnimatedSection>
            
            <p className="text-center text-text-muted text-sm mt-6">
              {t.realCases}
            </p>
          </div>
        </div>
      </section>

      {/* What is Lifting Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl">
          <AnimatedSection animation="fadeUp">
            <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-8">
              {t.whatIs}
            </h2>
            
            <p className="text-lg text-text-secondary mb-6">
              {t.whatIsDesc1}
            </p>
            
            <p className="text-lg text-text-secondary mb-8">
              {t.whatIsDesc2}
            </p>
          </AnimatedSection>

          <AnimatedSection animation="fadeUp" delay={0.1}>
            <h3 className="text-2xl font-light text-[#1C1C1A] mb-6">
              {t.isForYou}
            </h3>
            
            <ul className="space-y-4 mb-12">
              <li className="flex items-start gap-3">
                <Check className="text-[#8b7355] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t.sign1}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-[#8b7355] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t.sign2}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-[#8b7355] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t.sign3}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-[#8b7355] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t.sign4}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-[#8b7355] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t.sign5}</span>
              </li>
            </ul>
          </AnimatedSection>
        </div>
      </section>

      {/* NewLip Protocol Section */}
      <section className="py-20 bg-gradient-to-br from-[#1C1C1A] to-[#1a2a3f] px-4">
        <div className="container mx-auto max-w-6xl">
          <AnimatedSection animation="fadeUp" className="text-center mb-12">
            <span className="inline-block bg-[#8b7355] text-white px-4 py-1 rounded-none text-sm font-medium mb-4">
              {t.exclusiveProtocol}
            </span>
            <h2 className="text-3xl md:text-4xl font-light text-white mb-4">
              {t.newlipTitle}
            </h2>
            <p className="text-white/80 text-lg max-w-3xl mx-auto">
              {t.newlipDesc}
            </p>
          </AnimatedSection>
          
          <AnimatedSection animation="fadeUp" delay={0.1}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-ivory rounded-none shadow-2xl overflow-hidden">
                <img 
                  src="/images/newlip-resultado.jpg" 
                  alt="NewLip Result 1"
                  className="w-full h-auto"
                />
              </div>
              <div className="bg-ivory rounded-none shadow-2xl overflow-hidden">
                <img 
                  src="/images/newlip-resultado2.jpg" 
                  alt="NewLip Result 2"
                  className="w-full h-auto"
                />
              </div>
              <div className="bg-ivory rounded-none shadow-2xl overflow-hidden">
                <img 
                  src="/images/newlip-resultado3.jpg" 
                  alt="NewLip Result 3"
                  className="w-full h-auto"
                />
              </div>
              <div className="bg-ivory rounded-none shadow-2xl overflow-hidden">
                <img 
                  src="/images/newlip-resultado4.jpg" 
                  alt="NewLip Result 4"
                  className="w-full h-auto"
                />
              </div>
            </div>
          </AnimatedSection>
          
          <p className="text-center text-white/60 text-sm mb-8">
            {t.realCases}
          </p>
          
          <AnimatedSection animation="fadeUp" delay={0.2}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
              <div className="bg-ivory/10 backdrop-blur-sm rounded-none p-6">
                <div className="text-3xl font-bold text-[#d4af37] mb-2">+30</div>
                <p className="text-white/80">{t.yearsExp}</p>
              </div>
              <div className="bg-ivory/10 backdrop-blur-sm rounded-none p-6">
                <div className="text-3xl font-bold text-[#d4af37] mb-2">NewLip®</div>
                <p className="text-white/80">{t.patented}</p>
              </div>
              <div className="bg-ivory/10 backdrop-blur-sm rounded-none p-6">
                <div className="text-3xl font-bold text-[#d4af37] mb-2">100%</div>
                <p className="text-white/80">{t.naturalResults}</p>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-cream px-4">
        <div className="container mx-auto max-w-4xl">
          <AnimatedSection animation="fadeUp">
            <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-4">
              {t.benefits}
            </h2>
            
            <p className="text-lg text-text-secondary mb-8">
              {t.benefitsDesc}
            </p>
            
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <Check className="text-[#8b7355] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t.benefit1}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-[#8b7355] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t.benefit2}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-[#8b7355] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t.benefit3}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-[#8b7355] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t.benefit4}</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="text-[#8b7355] mt-1 flex-shrink-0" size={20} />
                <span className="text-text-secondary">{t.benefit5}</span>
              </li>
            </ul>
          </AnimatedSection>
        </div>
      </section>

      {/* Doctor Section */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <AnimatedSection animation="fadeLeft" className="lg:w-1/3">
              <img 
                src="/images/foto-angel-bata.webp"
                alt="Dr. Ángel Norro"
                className="w-full h-auto rounded-none shadow-lg"
              />
            </AnimatedSection>
            
            <AnimatedSection animation="fadeRight" className="lg:w-2/3">
              <h2 className="text-3xl md:text-4xl font-light text-[#1C1C1A] mb-6">
                {t.doctorTitle}
              </h2>
              
              <p className="text-text-secondary mb-4">
                {t.doctorDesc1}
              </p>
              
              <p className="text-text-secondary mb-4">
                {t.doctorDesc2}
              </p>
              
              <p className="text-text-secondary">
                {t.doctorDesc3}
              </p>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contacto" className="py-20 bg-[#1C1C1A] px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <AnimatedSection animation="fadeUp">
            <h2 className="text-3xl md:text-4xl font-light text-white mb-6">
              {t.ctaTitle}
            </h2>
            <p className="text-white/80 mb-8 text-lg">
              {t.ctaSubtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:952764547"
                className="inline-flex items-center justify-center gap-2 bg-ivory text-[#1C1C1A] px-8 py-4 rounded-none font-semibold hover:bg-gray-100 transition-colors"
              >
                <Phone size={20} />
                952 76 45 47
              </a>
              <a 
                href="https://wa.me/34696345792"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-gold500 text-white px-8 py-4 rounded-none font-semibold hover:bg-gold600 transition-colors"
              >
                WhatsApp
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default LiftingFacial;
